'use client';

import React from 'react';
import { cx } from '../../../utils/cx';

export type AdminPageProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageHeaderProps = React.HTMLAttributes<HTMLElement>;
export type AdminPageHeaderMainProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageHeaderTitleProps = React.HTMLAttributes<HTMLHeadingElement>;
export type AdminPageHeaderActionsProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageSectionProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageSectionHeaderProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageSectionActionsProps = React.HTMLAttributes<HTMLDivElement>;
export type AdminPageSectionSubtitleProps = React.HTMLAttributes<HTMLHeadingElement>;

export type AdminPageSectionTitleElement = 'h1' | 'h2';

export type AdminPageSectionTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
    as?: AdminPageSectionTitleElement;
};

export type AdminPageHeaderComponent = React.FC<AdminPageHeaderProps> & {
    Main: React.FC<AdminPageHeaderMainProps>;
    Title: React.FC<AdminPageHeaderTitleProps>;
    Actions: React.FC<AdminPageHeaderActionsProps>;
};

export type AdminPageSectionComponent = React.FC<AdminPageSectionProps> & {
    Header: React.FC<AdminPageSectionHeaderProps>;
    Title: React.FC<AdminPageSectionTitleProps>;
    Subtitle: React.FC<AdminPageSectionSubtitleProps>;
    Actions: React.FC<AdminPageSectionActionsProps>;
};

export type AdminPageComponent = React.FC<AdminPageProps> & {
    Header: AdminPageHeaderComponent;
    Section: AdminPageSectionComponent;
};

/**
 * Wraps a top-level admin dashboard page with consistent spacing.
 */
const AdminPageRoot: React.FC<AdminPageProps> = ({ children, className, ...props }) => (
    <div className={cx('RomaAdminPage flex min-h-full flex-col gap-2xl px-xl py-xl', className)} {...props}>
        {children}
    </div>
);

/**
 * Renders the standard admin dashboard page header.
 */
const AdminPageHeaderRoot: React.FC<AdminPageHeaderProps> = ({ children, className, ...props }) => (
    <header
        className={cx(
            'RomaAdminPageHeader flex flex-wrap items-center justify-between gap-lg border-b border-secondary pt-md pb-xl',
            className,
        )}
        {...props}
    >
        {children}
    </header>
);

/**
 * Wraps page identity content on the left of the admin dashboard header.
 */
const AdminPageHeaderMain: React.FC<AdminPageHeaderMainProps> = ({ children, className, ...props }) => (
    <div className={cx('flex min-w-0 flex-1 items-center gap-lg', className)} {...props}>
        {children}
    </div>
);

/**
 * Renders the standard admin dashboard page title.
 */
const AdminPageHeaderTitle: React.FC<AdminPageHeaderTitleProps> = ({ children, className, ...props }) => (
    <h1 className={cx('m-0 text-display-3xl text-primary', className)} {...props}>
        {children}
    </h1>
);

/**
 * Wraps page-level actions on the right of the admin dashboard header.
 */
const AdminPageHeaderActions: React.FC<AdminPageHeaderActionsProps> = ({ children, className, ...props }) => (
    <div className={cx('ml-auto flex flex-wrap items-center justify-end gap-lg', className)} {...props}>
        {children}
    </div>
);

const AdminPageHeader = Object.assign(AdminPageHeaderRoot, {
    Main: AdminPageHeaderMain,
    Title: AdminPageHeaderTitle,
    Actions: AdminPageHeaderActions,
}) as AdminPageHeaderComponent;

/**
 * Wraps an admin dashboard section with standard spacing.
 */
const AdminPageSectionRoot: React.FC<AdminPageSectionProps> = ({ children, className, ...props }) => (
    <section className={cx('RomaAdminPageSection flex flex-col gap-lg', className)} {...props}>
        {children}
    </section>
);

/**
 * Renders the header row for an admin dashboard section.
 */
const AdminPageSectionHeader: React.FC<AdminPageSectionHeaderProps> = ({ children, className, ...props }) => (
    <div className={cx('RomaAdminPageSectionHeader flex flex-row gap-lg', className)} {...props}>
        {children}
    </div>
);

/**
 * Gets the default typography for the requested section title element.
 */
const getSectionTitleClassName = (component: AdminPageSectionTitleElement) => {
    if (component === 'h1') return 'text-display-xl';
    return 'text-display-lg';
};

/**
 * Renders a section title using h2 by default, with h1 available via `as`.
 */
const AdminPageSectionTitle: React.FC<AdminPageSectionTitleProps> = ({
    as: Component = 'h2',
    children,
    className,
    ...props
}) => (
    <Component className={cx(getSectionTitleClassName(Component), className)} {...props}>
        {children}
    </Component>
);

/**
 * Renders a subsection title with standard admin dashboard typography.
 */
const AdminPageSectionSubtitle: React.FC<AdminPageSectionSubtitleProps> = ({ children, className, ...props }) => (
    <h3 className={cx('text-display-sm', className)} {...props}>
        {children}
    </h3>
);

/**
 * Wraps section-level actions and pushes them to the right of the header.
 */
const AdminPageSectionActions: React.FC<AdminPageSectionActionsProps> = ({ children, className, ...props }) => (
    <div className={cx('ml-auto flex flex-row gap-lg', className)} {...props}>
        {children}
    </div>
);

const AdminPageSection = Object.assign(AdminPageSectionRoot, {
    Header: AdminPageSectionHeader,
    Title: AdminPageSectionTitle,
    Subtitle: AdminPageSectionSubtitle,
    Actions: AdminPageSectionActions,
}) as AdminPageSectionComponent;

const AdminPage = Object.assign(AdminPageRoot, {
    Header: AdminPageHeader,
    Section: AdminPageSection,
}) as AdminPageComponent;

export { AdminPage };
